import { useState } from 'react';
import { Button, ButtonTheme, GenericAlertPopup } from '@apply/components';
import { ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../../../../../../../../../../ApplicationInsights';
import { ConfirmV2 } from '@apply/ui';

export interface RemoveUserButtonProps {
    propertyId: number;
    userId: string;
    onRefresh: () => void;
}

export const RemoveUserButton: React.FC<RemoveUserButtonProps> = ({ propertyId, userId, onRefresh }) => {
    const [removing, setRemoving] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    if (!propertyId) {
        return;
    }

    const handleRemoveClick = () => {
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        setShowConfirm(false);
        setRemoving(true);

        try {
            const applyApi = new ApplyApi(applicationInsights);
            const response = await applyApi.unassignPropertyDrawAuthorizedUser(propertyId);

            if (response.statusCode === 200) {
                GenericAlertPopup({
                    title: 'Success',
                    children: 'User removed successfully!',
                    width: '350px',
                });
                onRefresh();
            } else {
                const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to remove user';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Failed to remove user:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to remove user. Please try again.';
            GenericAlertPopup({
                title: 'Error',
                children: errorMessage,
                width: '350px',
            });
        } finally {
            setRemoving(false);
        }
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    return (
        <>
            <Button
                id={`unlinkUserButton_${propertyId}`}
                theme={ButtonTheme.LinkRed}
                height='20px'
                handleClick={handleRemoveClick}
                disabled={removing}
            >
                {removing ? 'Removing...' : 'Remove'}
            </Button>
            <ConfirmV2
                id='removeUserConfirm'
                title='Remove User'
                open={showConfirm}
                okLabel='Remove'
                cancelLabel='Cancel'
                width='400px'
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            >
                Are you sure you want to remove this user from the property? They will no longer be able to request draws for this property.
            </ConfirmV2>
        </>
    );
};
